import {
  AI_PLATFORM_DUOMI,
  AI_PLATFORM_LINGKEAGI,
  AI_PLATFORM_NOWCODING,
  AI_PLATFORM_PIDOI,
  AI_PLATFORM_SHIYUNAPI,
  normalizeCatalogPlatformId,
} from '../constants/aiPlatforms'

type ModelDisplayMeta = {
  label: string
  description: string
}

type OptionValueDisplayMeta = {
  label: string
  description?: string
}

type SizeDisplayMeta = {
  label: string
  description?: string
}

const MODEL_DISPLAY: Record<string, ModelDisplayMeta> = {
  [modelKey(AI_PLATFORM_DUOMI, 'gpt-image-2')]: {
    label: 'GPT Image 2',
    description: '文生图与参考图生成',
  },
  [modelKey(AI_PLATFORM_DUOMI, 'gemini-3.1-flash-image-preview')]: {
    label: 'Nano Banana 2',
    description: '文生图、参考图编辑与重绘，最高 2K',
  },
  [modelKey(AI_PLATFORM_DUOMI, 'gemini-3-pro-image-preview')]: {
    label: 'Nano Banana Pro',
    description: '文生图、参考图编辑与重绘，最高 4K',
  },
  [modelKey(AI_PLATFORM_DUOMI, 'gemini-2.5-pro-image-preview')]: {
    label: 'Nano Banana',
    description: '兼容版香蕉生图与编辑',
  },
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'gpt-image-2')]: {
    label: 'GPT Image 2',
    description: '文生图与参考图生成',
  },
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'gpt-image-2-all')]: {
    label: 'GPT Image 2 All',
    description: '参考图合并与编辑（最多5张）',
  },
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'dall-e-3')]: {
    label: 'DALL·E 3',
    description: 'OpenAI DALL·E 3 生图',
  },
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'grok-3-image')]: {
    label: 'Grok 3 Image',
    description: 'Grok 3 生图',
  },
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'flux-kontext-pro')]: {
    label: 'Flux Kontext Pro',
    description: 'Flux 生图',
  },
  [modelKey(AI_PLATFORM_NOWCODING, 'gpt-image-2')]: {
    label: 'GPT Image 2',
    description: '文生图与参考图生成',
  },
  [modelKey(AI_PLATFORM_PIDOI, 'deepseek-v4-flash')]: {
    label: 'DeepSeek V4 Flash',
    description: '文本推理与提示词整理',
  },
  [modelKey(AI_PLATFORM_LINGKEAGI, 'gpt-5-mini')]: {
    label: 'GPT-5 Mini',
    description: '文本推理与提示词整理',
  },
  [modelKey(AI_PLATFORM_LINGKEAGI, 'gpt-image-2')]: {
    label: 'GPT Image 2',
    description: '文生图与参考图生成',
  },
}

const OPTION_LABELS: Record<string, string> = {
  aspect_ratio: '宽高比',
  background: '背景',
  format: '文件格式',
  moderation: '审核',
  n: '生成数量',
  output_compression: '压缩级别',
  output_format: '输出格式',
  quality: '质量',
  style: '风格',
  thinking: '思考深度',
}

const OPTION_VALUE_LABELS: Record<string, Record<string, OptionValueDisplayMeta>> = {
  background: {
    auto: { label: '自动' },
    opaque: { label: '不透明' },
    transparent: { label: '透明' },
  },
  format: imageFormatLabels(),
  moderation: {
    auto: { label: '自动' },
    low: { label: '宽松' },
  },
  n: imageCountLabels(10),
  output_compression: {
    '100': { label: '100 · 最高' },
    '85': { label: '85 · 高' },
    '70': { label: '70 · 中' },
    '50': { label: '50 · 低' },
    '25': { label: '25 · 极低' },
  },
  output_format: imageFormatLabels(),
  quality: {
    auto: { label: '自动' },
    hd: { label: '高清' },
    high: { label: '高' },
    low: { label: '低' },
    medium: { label: '中' },
    standard: { label: '标准' },
  },
  style: {
    natural: { label: '自然' },
    vivid: { label: '生动' },
  },
  thinking: {
    auto: { label: '自动' },
    high: { label: '高' },
    low: { label: '低' },
  },
}

const MODEL_OPTION_VALUE_LABELS: Record<string, Record<string, Record<string, OptionValueDisplayMeta>>> = {
  [modelKey(AI_PLATFORM_LINGKEAGI, 'gpt-image-2')]: {
    quality: {
      auto: { label: '自适应' },
    },
  },
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'gpt-image-2')]: {
    n: {
      '10': { label: '10张', description: '诗云上限' },
    },
  },
}

const GENERIC_SIZE_DESCRIPTIONS: Record<string, string> = {
  '2048x2048': '2K正方形',
  '2048x1152': '2K横版',
  '3840x2160': '4K横版',
  '2160x3840': '4K竖版',
}

const MODEL_SIZE_LABELS: Record<string, Record<string, SizeDisplayMeta>> = {
  [modelKey(AI_PLATFORM_SHIYUNAPI, 'gpt-image-2')]: {
    auto: { label: '自动', description: '由模型自动选择画幅' },
  },
  [modelKey(AI_PLATFORM_NOWCODING, 'gpt-image-2')]: {
    auto: { label: '自动', description: '由模型自动选择画幅' },
  },
  [modelKey(AI_PLATFORM_LINGKEAGI, 'gpt-image-2')]: {
    auto: { label: '自适应', description: '根据参考图自动选择合适尺寸' },
    '1024x1024': { label: '正方形 (1:1)' },
    '1024x1536': { label: '竖版 (2:3)' },
    '1536x1024': { label: '横版 (3:2)' },
    '960x1280': { label: '竖版 (3:4)' },
    '1280x960': { label: '横版 (4:3)' },
    '1088x1920': { label: '竖版 (9:16)' },
    '1920x1088': { label: '横版 (16:9)' },
    '2048x2048': { label: '2K 正方形 (1:1)' },
    '2048x3072': { label: '2K 竖版 (2:3)' },
    '3072x2048': { label: '2K 横版 (3:2)' },
    '1920x2560': { label: '2K 竖版 (3:4)' },
    '2560x1920': { label: '2K 横版 (4:3)' },
    '1440x2560': { label: '2K 竖版 (9:16)' },
    '2560x1440': { label: '2K 横版 (16:9)' },
    '2880x2880': { label: '4K 正方形 (1:1)' },
    '2304x3456': { label: '4K 竖版 (2:3)' },
    '3456x2304': { label: '4K 横版 (3:2)' },
    '2400x3200': { label: '4K 竖版 (3:4)' },
    '3200x2400': { label: '4K 横版 (4:3)' },
    '2160x3840': { label: '4K 竖版 (9:16)' },
    '3840x2160': { label: '4K 横版 (16:9)' },
  },
}

function modelKey(platformId: string, modelId: string): string {
  return `${platformId}:${modelId}`
}

function imageFormatLabels(): Record<string, OptionValueDisplayMeta> {
  return {
    jpeg: { label: 'JPEG' },
    png: { label: 'PNG' },
    webp: { label: 'WebP' },
  }
}

function imageCountLabels(max: number): Record<string, OptionValueDisplayMeta> {
  const out: Record<string, OptionValueDisplayMeta> = {}
  for (let i = 1; i <= max; i += 1) {
    out[String(i)] = { label: `${i}张` }
  }
  return out
}

function normalizeValue(value: string): string {
  return value.replace('×', 'x').trim()
}

function displayModelKey(platformId: string, modelId: string): string {
  const normalizedPlatform = normalizeCatalogPlatformId(platformId) ?? platformId.trim()
  return modelKey(normalizedPlatform, modelId.trim())
}

function fallbackModelLabel(modelId: string): string {
  return modelId.trim() || '未命名模型'
}

function fallbackSizeLabel(value: string): string {
  const normalized = normalizeValue(value)
  if (!normalized) return ''
  if (normalized.toLowerCase() === 'auto') return '自动'
  return normalized.replace(/x/i, '×')
}

export function resolveModelDisplay(platformId: string, modelId: string): ModelDisplayMeta {
  return MODEL_DISPLAY[displayModelKey(platformId, modelId)] ?? {
    label: fallbackModelLabel(modelId),
    description: '',
  }
}

export function resolveSizeDisplay(platformId: string, modelId: string, value: string): SizeDisplayMeta {
  const normalized = normalizeValue(value)
  const modelMeta = MODEL_SIZE_LABELS[displayModelKey(platformId, modelId)]?.[normalized]
  if (modelMeta) return modelMeta
  return {
    label: fallbackSizeLabel(normalized),
    description: GENERIC_SIZE_DESCRIPTIONS[normalized],
  }
}

export function resolveAspectRatioDisplay(value: string): string {
  const normalized = normalizeValue(value)
  if (normalized.toLowerCase() === 'auto') return '自动'
  return normalized
}

export function resolveOptionLabel(key: string): string {
  const normalized = key.trim()
  return OPTION_LABELS[normalized] ?? normalized
}

export function resolveOptionValueDisplay(
  platformId: string,
  modelId: string,
  optionKey: string,
  value: string,
): OptionValueDisplayMeta {
  const normalizedOptionKey = optionKey.trim()
  const normalizedValue = normalizeValue(value)
  return (
    MODEL_OPTION_VALUE_LABELS[displayModelKey(platformId, modelId)]?.[normalizedOptionKey]?.[normalizedValue] ??
    OPTION_VALUE_LABELS[normalizedOptionKey]?.[normalizedValue] ?? {
      label: normalizedValue,
    }
  )
}
